import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import {
  evictAffectedPrices,
  evictParentPricesAfterDeletion
} from "shared/api/price/evictPriceCache"
import { invalidateParentPricesAfterDeletion } from "shared/api/price/modifyPriceCache"
import {
  DeleteResponseTypename,
  MutationSchema,
  MutationTypename
} from "shared/api/types"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProductPositionTypename } from "shared/types/Project"
import { DELETE_PRODUCT_POSITION } from "shared/types/ProjectMutations"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useFavoritesLazyQuery } from "entities/favorites/graphql/queries.generated"
import {
  DeleteProductPositionDocument,
  DeleteProductPositionMutation,
  DeleteProductPositionMutationVariables
} from "entities/product-positions/graphql/mutations.generated"
import { resolveProjectChildParent } from "entities/project-children/lib/resolveProjectChildParent"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import z from "zod"

import { evictProductPosition } from "../lib/evictProductPosition"
import { DeleteProductPositionVariablesSchema } from "../types/ProductPosition"

const DeleteProductPositionResponseSchema = MutationSchema.extend({
  productPosition: z.object({
    __typename: z.literal(DeleteResponseTypename),
    success: z.boolean()
  })
})

export function useDeleteProductPosition() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.ProductPosition.DELETE_PRODUCT_POSITION.Start"),
      success: t("Request.ProductPosition.DELETE_PRODUCT_POSITION.Success"),
      failure: t("Request.ProductPosition.DELETE_PRODUCT_POSITION.Failure")
    }
  })
  const { raiseLoadingToast: raiseFavoriteLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Favorite.DELETE_PRODUCT_POSITION.Start"),
      success: t("Request.Favorite.DELETE_PRODUCT_POSITION.Success"),
      failure: t("Request.Favorite.DELETE_PRODUCT_POSITION.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjectChildren] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [fetchFavorites] = useFavoritesLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const [apolloMutate, rest] = useApolloMutation<
    DeleteProductPositionMutation,
    DeleteProductPositionMutationVariables
  >(DeleteProductPositionDocument)

  const resolveParent = useEvent((projectID: string, childID: string) =>
    resolveProjectChildParent({
      projectID,
      childID,
      cache: apolloCache,
      refetch: () =>
        projectID === FAVORITES_PROJECT_ID
          ? fetchFavorites()
          : fetchProjectChildren({ variables: { id: projectID } })
    })
  )

  const deleteProductPosition = useEvent(
    async (
      variables: DeleteProductPositionMutationVariables,
      projectID: string
    ) => {
      const isFavorite = projectID === FAVORITES_PROJECT_ID
      const { raiseSuccessToast, raiseErrorToast } = isFavorite
        ? raiseFavoriteLoadingToast()
        : raiseLoadingToast()
      const parent = await resolveParent(projectID, variables.id)

      invalidateParentPricesAfterDeletion({
        context: {
          projectID,
          parentID: parent.id,
          itemID: variables.id,
          itemType: ProductPositionTypename,
          parentType: parent.__typename
        },
        cache: apolloCache
      })

      evictProductPosition(variables.id, apolloCache)

      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DELETE_PRODUCT_POSITION
      })

      return callMutation(apolloMutate, {
        variables,
        transform: data => data.productPosition,
        schemas: {
          input: DeleteProductPositionVariablesSchema,
          response: DeleteProductPositionResponseSchema
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            productPosition: {
              __typename: DeleteResponseTypename,
              success: true
            }
          },
          onCompleted: () => {
            evictProductPosition(variables.id, apolloCache)

            evictParentPricesAfterDeletion({
              context: {
                projectID,
                parentID: parent.id,
                itemID: variables.id,
                itemType: ProductPositionTypename,
                parentType: parent.__typename
              },
              cache: apolloCache
            })

            evictAffectedPrices({
              projectID,
              itemID: variables.id,
              itemType: ProductPositionTypename,
              cache: apolloCache
            })

            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [deleteProductPosition, rest] as [
    typeof deleteProductPosition,
    typeof rest
  ]
}
